import React, { useState } from 'react';
import { Modal, Form, Input, notification } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { hazardService } from '@iso/services/hazard.service';
import { getSafetyActivityHazardLabel } from '@iso/constants/hazardTypes';

const { TextArea } = Input;

/**
 * Reopen Hazard Modal
 * Allows user to reopen a resolved hazard with a reason
 */
const ReopenHazardModal = ({ visible, hazardId, hazardTitle, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const result = await hazardService.reopenHazard(
        hazardId,
        values.reason
      );

      if (result && result.code === '0000') {
        notification.success({
          message: 'Success',
          description: 'Hazard has been reopened and reassigned',
          icon: <ReloadOutlined style={{ color: '#1890ff' }} />
        });
        form.resetFields();
        if (onSuccess) onSuccess();
      } else {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to reopen hazard',
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancel) onCancel();
  };

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
          Reopen Hazard
        </span>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Reopen"
      okButtonProps={{
        icon: <ReloadOutlined />,
        danger: true
      }}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <p><strong>Hazard:</strong> {getSafetyActivityHazardLabel(hazardTitle)}</p>
        <p style={{ color: '#faad14' }}>
          <ExclamationCircleOutlined /> Reopening this hazard will reset it to Level 1 and create a new assignment.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <Form.Item
          name="reason"
          label="Reason for Reopening"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Please provide a reason for reopening this hazard'
            },
            {
              min: 10,
              message: 'Reason must be at least 10 characters'
            }
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Explain why this hazard needs to be reopened..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReopenHazardModal;
